import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowDownUp, Bookmark, CheckSquare2, ChevronRight, CirclePlay, Filter,
  ListFilter, Search, Settings2, Square, Trash2, UserCheck, UserRoundSearch, XCircle,
} from 'lucide-react';
import { api } from '../api.js';
import { Avatar, Badge, Button, Callout, Confidence, EmptyState, IconButton, Modal, PageHeader, ScoreRing } from '../components/ui.jsx';
import {
  classNames, getCandidateConfidence, getCandidateRecommendation, getCandidateScore, scoreTone,
  statusLabel, workflowStatusLabel, workflowStatusTone,
} from '../utils.js';

const FILTERS = [
  { id: 'all', label: '全部' },
  { id: 'needs_review', label: '需复核' },
  { id: 'backup', label: '备选' },
  { id: 'recommended', label: '推荐面试' },
  { id: 'scheduling', label: '待约面' },
  { id: 'interview_scheduled', label: '待面试' },
  { id: 'offer', label: 'Offer' },
  { id: 'not_match', label: '不匹配' },
];

const QUICK_ACTIONS = [
  { status: 'backup', label: '设为备选', icon: Bookmark },
  { status: 'recommended', label: '推荐面试', icon: UserCheck },
  { status: 'not_match', label: '设为不匹配', icon: XCircle },
];

function isOfferStatus(status) {
  return ['offer_ready', 'offer_sent', 'offer_accepted', 'offer_declined', 'hired'].includes(status);
}

export default function CandidatesPage({ state, routeJobId, navigate, refresh, notify }) {
  const jobs = state?.jobs || [];
  const allCandidates = state?.candidates || [];
  const currentUser = state?.currentUser;
  const [jobFilter, setJobFilter] = useState(routeJobId || 'all');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('score');
  const [selected, setSelected] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [busyCandidate, setBusyCandidate] = useState('');
  const [bulkBusy, setBulkBusy] = useState('');
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [aiSetupOpen, setAiSetupOpen] = useState(false);
  const assessmentModelReady = Boolean(state?.system?.aiReadiness?.assessment ?? state?.system?.aiReadiness?.scoring);

  useEffect(() => { setJobFilter(routeJobId || 'all'); }, [routeJobId]);

  const openCandidate = (candidateId) => navigate(
    'candidate', candidateId, jobFilter === 'all' ? null : jobFilter,
  );

  const candidates = useMemo(() => allCandidates.filter((candidate) => jobFilter === 'all' || candidate.jobId === jobFilter), [allCandidates, jobFilter]);
  const incomplete = candidates.filter((candidate) => candidate.source !== 'demo'
    && (['ready', 'queued', 'failed'].includes(candidate.status)
      || (candidate.status === 'needs_review' && candidate.totalScore === null)));

  const visible = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    const filtered = candidates.filter((candidate) => {
      const workflow = candidate.workflowStatus || 'needs_review';
      if (filter === 'offer' && !isOfferStatus(workflow)) return false;
      if (!['all', 'offer'].includes(filter) && workflow !== filter) return false;
      return !query || candidate.displayName.toLocaleLowerCase().includes(query)
        || candidate.jobTitle?.toLocaleLowerCase().includes(query)
        || candidate.summary?.toLocaleLowerCase().includes(query)
        || candidate.risks?.some((risk) => risk.title.toLocaleLowerCase().includes(query));
    });
    return [...filtered].sort((a, b) => {
      if (sort === 'confidence') return getCandidateConfidence(b) - getCandidateConfidence(a);
      if (sort === 'risk') return (b.risks?.length || 0) - (a.risks?.length || 0);
      if (sort === 'updated') return String(b.workflowUpdatedAt || b.updatedAt).localeCompare(String(a.workflowUpdatedAt || a.updatedAt));
      return (getCandidateScore(b) ?? -1) - (getCandidateScore(a) ?? -1);
    });
  }, [candidates, filter, search, sort]);

  const selectedCandidates = useMemo(
    () => candidates.filter((candidate) => selected.includes(candidate.id)),
    [candidates, selected],
  );
  const commonQuickActions = QUICK_ACTIONS.filter((action) => selectedCandidates.length > 0
    && selectedCandidates.every((candidate) => {
      const workflow = candidate.workflowStatus || 'needs_review';
      return (state?.workflow?.allowedTransitions?.[workflow] || []).includes(action.status);
    }));

  const toggleAll = () => setSelected(selected.length === visible.length ? [] : visible.map((item) => item.id));
  const toggle = (id) => setSelected((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);

  const processAll = async () => {
    if (!assessmentModelReady) {
      setAiSetupOpen(true);
      return;
    }
    setProcessing(true);
    try {
      const targetJobs = jobFilter === 'all' ? jobs : jobs.filter((job) => job.id === jobFilter);
      const results = await Promise.all(targetJobs.map((job) => api.processAll(job.id)));
      const queued = results.reduce((sum, result) => sum + Number(result.queued || 0), 0);
      notify(queued ? `${queued} 位候选人已进入 AI 评估队列` : '当前没有需要处理的候选人');
      await refresh();
    } catch (error) { notify(error.message, 'danger'); }
    finally { setProcessing(false); }
  };

  const transition = async (candidate, toStatus) => {
    setBusyCandidate(`${candidate.id}:${toStatus}`);
    try {
      await api.transitionCandidate(candidate.id, { actorId: currentUser.id, toStatus });
      notify(`${candidate.displayName} 已更新为“${workflowStatusLabel(toStatus)}”`);
      await refresh();
    } catch (error) { notify(error.message, 'danger'); }
    finally { setBusyCandidate(''); }
  };

  const transitionSelected = async (toStatus) => {
    if (!selectedCandidates.length) return;
    setBulkBusy(toStatus);
    try {
      const result = await api.transitionCandidates(selectedCandidates.map((candidate) => candidate.id), { toStatus });
      notify(`已批量更新 ${result.updated} 位候选人为“${workflowStatusLabel(toStatus)}”`);
      setSelected([]);
      setBulkConfirm(null);
      await refresh();
    } catch (error) { notify(error.message, 'danger'); }
    finally { setBulkBusy(''); }
  };

  const deleteSelected = async () => {
    if (!selectedCandidates.length) return;
    setBulkBusy('delete');
    try {
      const result = await api.deleteCandidates(selectedCandidates.map((candidate) => candidate.id));
      notify(`已删除 ${result.deleted} 位候选人`);
      setSelected([]);
      setBulkConfirm(null);
      await refresh();
    } catch (error) { notify(error.message, 'danger'); }
    finally { setBulkBusy(''); }
  };

  const deleteCandidate = async () => {
    if (!deleteTarget) return;
    setBusyCandidate(`${deleteTarget.id}:delete`);
    try {
      await api.deleteCandidate(deleteTarget.id);
      setSelected((current) => current.filter((id) => id !== deleteTarget.id));
      notify(`${deleteTarget.displayName} 已删除`);
      setDeleteTarget(null);
      await refresh();
    } catch (error) { notify(error.message, 'danger'); }
    finally { setBusyCandidate(''); }
  };

  const countForFilter = (id) => candidates.filter((candidate) => {
    const status = candidate.workflowStatus || 'needs_review';
    if (id === 'all') return true;
    if (id === 'offer') return isOfferStatus(status);
    return status === id;
  }).length;

  return (
    <div className="page candidates-page">
      <PageHeader
        eyebrow={`${jobs.length} 个可访问岗位 · ${candidates.length} 位候选人`}
        title="候选人工作流"
        description="AI 建议与人工招聘阶段分开管理"
        actions={<>
          <label className="page-job-select"><Filter size={16} /><select value={jobFilter} onChange={(event) => { const nextJobId = event.target.value; setJobFilter(nextJobId); setSelected([]); navigate('candidates', nextJobId === 'all' ? null : nextJobId); }} aria-label="岗位筛选"><option value="all">全部岗位</option>{jobs.map((job) => <option key={job.id} value={job.id}>{job.title}</option>)}</select></label>
          <Button icon={CirclePlay} loading={processing} disabled={!incomplete.length} onClick={processAll}>评估未完成项{incomplete.length ? ` (${incomplete.length})` : ''}</Button>
          {currentUser?.role !== 'hr' && <Button variant="primary" icon={UserRoundSearch} onClick={() => navigate('calibration')}>人工复核</Button>}
        </>}
      />

      {incomplete.length > 0 && <Callout tone="warning" title={`${incomplete.length} 位候选人尚未完成 AI 评估`} action={<Button size="sm" icon={CirclePlay} loading={processing} onClick={processAll}>开始处理</Button>}>材料已归档，但解析或模型评估尚未完成。</Callout>}

      <div className="table-toolbar">
        <div className="segmented-control" aria-label="招聘阶段筛选">{FILTERS.map((item) => (
          <button key={item.id} className={filter === item.id ? 'active' : ''} onClick={() => { setFilter(item.id); setSelected([]); }}>{item.label}<span>{countForFilter(item.id)}</span></button>
        ))}</div>
        <div className="table-tools">
          <label className="search-field"><Search size={16} /><input value={search} onChange={(event) => { setSearch(event.target.value); setSelected([]); }} placeholder="搜索姓名、岗位、风险或摘要" /></label>
          <label className="sort-select"><ArrowDownUp size={15} /><select value={sort} onChange={(event) => setSort(event.target.value)}><option value="score">按 AI 分数</option><option value="updated">按阶段更新时间</option><option value="confidence">按置信度</option><option value="risk">按风险</option></select></label>
        </div>
      </div>

      {selectedCandidates.length > 0 && <div className="bulk-bar">
        <CheckSquare2 size={17} /><strong>已选择 {selectedCandidates.length} 人</strong><span />
        <div className="bulk-actions">
          {commonQuickActions.map((action) => <button key={action.status} className={classNames('bulk-action', action.status === 'recommended' && 'bulk-recommend', action.status === 'not_match' && 'bulk-reject')} disabled={Boolean(bulkBusy)} onClick={() => action.status === 'not_match' ? setBulkConfirm({ type: 'workflow', action }) : transitionSelected(action.status)}><action.icon size={14} />{action.label}</button>)}
          <button className="bulk-action bulk-delete" disabled={Boolean(bulkBusy)} onClick={() => setBulkConfirm({ type: 'delete' })}><Trash2 size={14} />删除候选人</button>
        </div>
        <button className="bulk-clear" disabled={Boolean(bulkBusy)} onClick={() => setSelected([])}>取消选择</button>
      </div>}

      <div className="candidate-table-wrap">
        {visible.length === 0 ? <EmptyState icon={ListFilter} title="没有符合条件的候选人" description="调整岗位或阶段筛选后重试。" /> : (
          <table className="candidate-table candidate-workflow-table">
            <thead><tr><th className="check-cell"><button onClick={toggleAll} aria-label="全选">{selected.length === visible.length ? <CheckSquare2 size={17} /> : <Square size={17} />}</button></th><th>候选人 / 岗位</th><th>AI 排序分</th><th>AI 建议</th><th>人工招聘阶段</th><th>证据与风险</th><th>快捷操作</th><th aria-label="查看" /></tr></thead>
            <tbody>{visible.map((candidate, index) => {
              const score = getCandidateScore(candidate);
              const recommendation = getCandidateRecommendation(candidate);
              const workflow = candidate.workflowStatus || 'needs_review';
              const allowed = state?.workflow?.allowedTransitions?.[workflow] || [];
              return <tr key={candidate.id} className={classNames(selected.includes(candidate.id) && 'selected')} onDoubleClick={() => openCandidate(candidate.id)}>
                <td className="check-cell"><button onClick={() => toggle(candidate.id)} aria-label={`选择 ${candidate.displayName}`}>{selected.includes(candidate.id) ? <CheckSquare2 size={17} /> : <Square size={17} />}</button></td>
                <td><div className="candidate-identity"><Avatar name={candidate.displayName} tone={index} /><div><button onClick={() => openCandidate(candidate.id)}>{candidate.displayName}</button><span>{candidate.jobTitle} · {candidate.assetCount} 份材料</span></div></div></td>
                <td><div className="table-score"><ScoreRing score={score} size={46} />{candidate.manualAdjustedScore !== null && <Badge tone="neutral">人工校准</Badge>}</div></td>
                <td><div className="ai-suggestion-cell"><Badge tone={recommendation === '不匹配' ? 'danger' : recommendation.includes('推荐') ? 'success' : recommendation === '继续复核' ? 'warning' : scoreTone(score)}>{recommendation}</Badge><small>{statusLabel(candidate.status)}</small></div></td>
                <td><Badge tone={workflowStatusTone(workflow)}>{workflowStatusLabel(workflow)}</Badge></td>
                <td><div className="evidence-risk-cell"><Confidence value={getCandidateConfidence(candidate)} compact />{candidate.risks?.length > 0 && <span className={`risk-count risk-${candidate.risks[0].severity}`}><AlertTriangle size={14} />{candidate.risks.length}</span>}</div></td>
                <td><div className="candidate-row-actions" onDoubleClick={(event) => event.stopPropagation()}>{QUICK_ACTIONS.filter((action) => allowed.includes(action.status)).map((action) => <IconButton key={action.status} icon={action.icon} label={action.label} className={action.status === 'recommended' ? 'active-recommend' : action.status === 'not_match' ? 'active-reject' : ''} disabled={Boolean(busyCandidate)} onClick={() => transition(candidate, action.status)} />)}<IconButton icon={Trash2} label="删除候选人" variant="danger-ghost" disabled={Boolean(busyCandidate)} onClick={() => setDeleteTarget(candidate)} /></div></td>
                <td><IconButton icon={ChevronRight} label="查看候选人" onClick={() => openCandidate(candidate.id)} /></td>
              </tr>;
            })}</tbody>
          </table>
        )}
      </div>
      <div className="table-foot"><span>显示 {visible.length} / {candidates.length} 位候选人</span><span>AI 建议不会自动改变人工招聘阶段</span></div>

      <Modal open={Boolean(deleteTarget)} title={`删除候选人 · ${deleteTarget?.displayName || ''}`} description="将删除候选人、材料、评估、面试与通知记录，且无法撤销。" onClose={() => !busyCandidate && setDeleteTarget(null)} footer={<><Button variant="ghost" disabled={Boolean(busyCandidate)} onClick={() => setDeleteTarget(null)}>取消</Button><Button variant="danger-ghost" icon={Trash2} loading={busyCandidate === `${deleteTarget?.id}:delete`} onClick={deleteCandidate}>确认删除</Button></>}>
        <Callout tone="danger" title="确认删除全部候选人数据">岗位和其他候选人不会受影响。</Callout>
      </Modal>

      <Modal
        open={Boolean(bulkConfirm)}
        title={bulkConfirm?.type === 'delete' ? '批量删除候选人' : '批量设为不匹配'}
        description={bulkConfirm?.type === 'delete' ? '将批量删除所选候选人、材料、评估、面试与通知记录，且无法撤销。' : '该操作会更新所选候选人的人工招聘阶段，但不会改变 AI 原始评分。'}
        onClose={() => !bulkBusy && setBulkConfirm(null)}
        footer={<><Button variant="ghost" disabled={Boolean(bulkBusy)} onClick={() => setBulkConfirm(null)}>取消</Button><Button variant={bulkConfirm?.type === 'delete' ? 'danger-ghost' : 'secondary'} icon={bulkConfirm?.type === 'delete' ? Trash2 : XCircle} loading={Boolean(bulkBusy)} onClick={() => bulkConfirm?.type === 'delete' ? deleteSelected() : transitionSelected(bulkConfirm?.action?.status)}>确认执行</Button></>}
      >
        <Callout tone={bulkConfirm?.type === 'delete' ? 'danger' : 'warning'} title={bulkConfirm?.type === 'delete' ? `确认删除 ${selectedCandidates.length} 位候选人的全部数据` : `确认将 ${selectedCandidates.length} 位候选人设为“不匹配”`}>批量操作会记录到每位候选人的流程历史和审计日志。</Callout>
      </Modal>

      <Modal open={aiSetupOpen} title="需要先配置 AI Provider" description="当前没有可用于简历审核的 AI Provider 和模型。" onClose={() => setAiSetupOpen(false)} footer={<><Button variant="ghost" onClick={() => setAiSetupOpen(false)}>关闭</Button>{currentUser?.role === 'admin' && <Button variant="primary" icon={Settings2} onClick={() => navigate('models')}>前往模型与处理</Button>}</>}>
        <Callout tone="warning" title="无法开始处理">请为“审核模型”选择已启用的 Provider 和 Model，并配置 API Base URL 与 API Key。{currentUser?.role !== 'admin' ? '请联系管理员完成配置。' : ''}</Callout>
      </Modal>
    </div>
  );
}
